/**
 * Created by Ning Jiang on 1/21/2020.
 */

import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineLite, TweenLite} from "gsap";
import {DWBackground} from "../../background/DWBackground";
import {DeadwoodGameConfig} from "../../DeadwoodGameConfig";
import {DeadwoodSoundConfig} from "../../DeadwoodSoundConfig";

export class BonusWinPresentationView extends BaseView {

    private _introLayer:ScalableLayer;
    private _introContainer:PIXI.Container;
    private _panelSprite:PIXI.Sprite;
    private _panelSpine:PIXI.spine.Spine;
    private _glowAddSpine:PIXI.spine.Spine;
    private _shootOutSpine:PIXI.spine.Spine;
    private _sparkAddSpine:PIXI.spine.Spine;

    private _hotWaveSprite:PIXI.Sprite;
    private _hotWaveSpine:PIXI.spine.Spine;

    private _isIntroPlaying:boolean = false;
    private _mode:string;

    constructor() {
        super();
    }

    protected initAnimations():void {
        this._introLayer = StageManager.getLayer(DeadwoodGameConfig.instance.LAYERS.BONUS_INTRO.name);

        this._introContainer = new PIXI.Container();
        this._introContainer.alpha = 0;
        this._introContainer.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        this._introLayer.addChild(this._introContainer);

        this._panelSprite = new PIXI.Sprite();
        this._panelSprite.anchor.set(0.5);
        this._introContainer.addChild(this._panelSprite);

        this._panelSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("shootOutPanel"));
        this._panelSpine.autoUpdate = true;
        this._panelSprite.addChild(this._panelSpine);

        this._glowAddSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("shootOut_glowAdd"));
        this._glowAddSpine.autoUpdate = true;
        this._panelSprite.addChild(this._glowAddSpine);

        this._shootOutSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("shootOut_name"));
        this._shootOutSpine.autoUpdate = true;
        this._panelSprite.addChild(this._shootOutSpine);

        this._sparkAddSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("shootOut_sparkAdd"));
        this._sparkAddSpine.autoUpdate = true;
        this._panelSprite.addChild(this._sparkAddSpine);
    }

    public addIntroToTimeline(timeline:TimelineLite):void {
        timeline.add(() => {
            this._isIntroPlaying = true;
            this._panelSpine.state.clearTrack(0);
            this._panelSpine.skeleton.setToSetupPose();
            this._glowAddSpine.state.clearTrack(0);
            this._glowAddSpine.skeleton.setToSetupPose();
            this._shootOutSpine.state.clearTrack(0);
            this._shootOutSpine.skeleton.setToSetupPose();
            this._sparkAddSpine.state.clearTrack(0);
            this._sparkAddSpine.skeleton.setToSetupPose();
            EventHandler.dispatchEvent(new GameEvent(DWBackground.DIM_STAGE, 0.7, 0.5));
        });

        timeline.add([
            TweenLite.to(this._introContainer, 0.3, {alpha : 1}),
            new SpineTween(this._panelSpine, 0, "animation"),
            new SpineTween(this._glowAddSpine, 0, "animation"),
            new SpineTween(this._sparkAddSpine, 0, "animation"),
            () => {
                this._shootOutSpine.state.setAnimation(0, "animation", false);
                this._shootOutSpine.alpha = 1;
                SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.SHOOT_OUT_INTRO);
            }
        ]);
        timeline.addLabel("displayed", 0.57);
        timeline.add(TweenLite.to(this._introContainer, 0.3, {alpha : 0}), "+=0.5"); // show it for a while
        timeline.add(() => {
            this._panelSpine.state.setEmptyAnimation(0, 0);
            this._glowAddSpine.state.setEmptyAnimation(0, 0);
            this._shootOutSpine.state.setEmptyAnimation(0, 0);
            this._sparkAddSpine.state.setEmptyAnimation(0, 0);
            this._isIntroPlaying = false;
        });
    }

    public addWildsEffectsToTimeline(timeline:TimelineLite):void {
        if(!this._hotWaveSprite) {
            const hotWaveLayer:ScalableLayer = StageManager.getLayer(DeadwoodGameConfig.instance.LAYERS.REEL_BACKGROUND.name);

            this._hotWaveSprite = new PIXI.Sprite();
            this._hotWaveSprite.anchor.set(0.5);
            const positionX:number = GameConfig.instance.REEL_DISPLAY_POSITIONS[2][0] + GameConfig.instance.REEL_AREA_POS_X;
            const positionY:number = GameConfig.instance.REEL_DISPLAY_POSITIONS[2][1] + GameConfig.instance.SYMBOL_HEIGHT * 2;
            this._hotWaveSprite.position.set(positionX, positionY);
            this._hotWaveSprite.alpha = 0;
            hotWaveLayer.addChild(this._hotWaveSprite);

            (<PIXI.Texture>PIXI.utils.TextureCache["bonus/redBg"]).baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
            this._hotWaveSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("shootOut_hotWave"));
            this._hotWaveSpine.autoUpdate = true;
            this._hotWaveSprite.addChild(this._hotWaveSpine);

            const mask:PIXI.Graphics = new PIXI.Graphics();
            mask.beginFill(0x0000ff);
            mask.drawRect(GameConfig.instance.REEL_AREA_POS_X, DeadwoodGameConfig.instance.REEL_AREA_TOP, GameConfig.instance.REEL_AREA_WIDTH, DeadwoodGameConfig.instance.REEL_AREA_ACTUAL_HEIGHT);
            mask.endFill();
            hotWaveLayer.addChild(mask);
            this._hotWaveSprite.mask = mask;
        }

        timeline.add([
            () => {
                this._hotWaveSpine.state.clearTrack(0);
                this._hotWaveSpine.state.setAnimation(0, "animation", false).onComplete = () => {
                    TweenLite.to(this._hotWaveSprite, 0.1, {alpha : 0})
                };
                SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.SHOOT_OUT_FIRE);
            },
            TweenLite.to(this._hotWaveSprite, 0.2, {alpha : 1})
        ], 0);
    }

    public setMode(mode:string):void {
        this._mode = mode;
    }
}